import { AgentDesktopRouter } from '@agent/agent-interface/agent-desktop-router';
import { REJECTION_PENDING_ASK_FOR_HELP } from '@agent/core/channel-digit/mail/eptica/eptica.helper';
import { EpticaHelperJson } from '@agent/core/channel-digit/mail/eptica/eptica.helper.json';
import { EpticaData } from '@agent/core/channel-digit/mail/eptica/models/eptica-data';
import { EpticaRequest } from '@agent/core/channel-digit/mail/eptica/models/eptica.request.incoming';
import { IEpticaFrame, IEpticaJsonResponse } from '@agent/core/channel-digit/mail/eptica/models/eptica.request.interfaces';
import { IContactDeprecatedService } from '@agent/core/contact/contact-deprecated.service';
import { ToastDeprecatedService } from '@commons/core/notification/toast-deprecated.service';
import { IContact } from '@commons/models/contact';
import { ng1Destroy } from '@commons/util/utilities/rx';
import { IComponentController, ILogService, IPromise, IQService } from 'angular';

import {
  DisplayDeprecatedComponent,
  firstOfArr,
  isNotNullOrUndefined,
  None,
  Option,
  reverseOfArr,
  splitOfStr,
  TranslateService,
} from '@contact/upgrade';

import { AgentBookinMailInprocessState } from './state/agent-booking-mail-routing.module';

export abstract class AbstractEpticaController<FrameType extends IEpticaFrame>
  extends DisplayDeprecatedComponent
  implements IComponentController
{
  protected responseId: string;
  protected showActions: boolean = false;
  protected state: string;

  constructor(
    protected readonly $log: ILogService,
    protected readonly $q: IQService,
    protected readonly contactDeprecatedService: IContactDeprecatedService,
    protected readonly toastDeprecatedService: ToastDeprecatedService,
    protected readonly router: AgentDesktopRouter,
    protected readonly translateService: TranslateService
  ) {
    super();
  }

  $onInit() {
    this.router.currentUrl$
      .pipe(isNotNullOrUndefined(), splitOfStr('/'), reverseOfArr(), firstOfArr(), isNotNullOrUndefined(), ng1Destroy(this))
      .subscribe(state => (this.state = state));
  }

  getCurrentStateName(): string {
    return this.state;
  }

  /**
   * Check this current state
   */
  isState(state: AgentBookinMailInprocessState): boolean {
    return this.state === state;
  }

  /**
   * Checks the eptica data to find out whether we are on the right eptica page
   */
  protected abstract checkEpticaDataAfterReload(
    request: FrameType,
    epticaData: EpticaData,
    refuseReason?: Option<string>
  ): IPromise<EpticaData>;

  /**
   * Return the request promise
   */
  protected abstract mainPromise(): IPromise<FrameType>;

  /**
   * Post a form and perfoms the checks of eptica data
   */
  protected doSubmitAction(requestAction: (request: FrameType) => IPromise<EpticaData>, refuseReason?: string): IPromise<EpticaData> {
    this.showActions = false;
    return this.mainPromise().then(request => {
      request.hide();
      let actionPromise = requestAction(request);
      if (request instanceof EpticaRequest) {
        actionPromise.catch(e => {
          if (e && e.cause === REJECTION_PENDING_ASK_FOR_HELP) {
            // Send was not done because the popup that asks to confirm to close all the pending
            // assistance requests has opened. => override the vanilla behaviour by replacing the buttons
            request.overrideClosePendingAssist(() => this.doSubmitAction(() => request.confirmClosePendingAssist(e.confirmButtonId)));
          }
          return this.$q.reject(e);
        });
      }
      return actionPromise.then(
        epticaData => this.checkEpticaDataAfterReload(request, epticaData, Option.of(refuseReason)),
        () => {
          request.show();
          this.showActions = true;
          return this.$q.reject(request);
        },
        () => this.$log.debug('Action finished')
      );
    });
  }

  protected handleJsonAction(
    requestAction: (request: FrameType) => IPromise<IEpticaJsonResponse>,
    refuseReason?: string,
    isSuspend = false,
    isApproval = false
  ): IPromise<IEpticaJsonResponse> {
    this.showActions = false;
    return this.mainPromise().then(request => {
      request.hide();
      let actionPromise = requestAction(request);
      if (request instanceof EpticaRequest) {
        actionPromise.catch(e => {
          if (e && e.cause === REJECTION_PENDING_ASK_FOR_HELP) {
            // Send was not done because the popup that asks to confirm to close all the pending
            // assistance requests has opened. => override the vanilla behaviour by replacing the buttons
            request.overrideClosePendingAssistJson(() => this.handleJsonAction(() => request.confirmClosePendingAssistJson(e.submitForm)));
          }
          return this.$q.reject(e);
        });
      }
      return actionPromise.then(
        epticaJson =>
          this.doPostJsonActions(request, epticaJson, refuseReason).then(e =>
            this.handleJsonSuccess(request, e, refuseReason, isSuspend, isApproval)
          ),
        (errorJsonArray: IEpticaJsonResponse[]) => this.showFrameAndReject(request, errorJsonArray),
        () => this.$log.debug('Action finished')
      );
    });
  }

  protected handleJsonActions(
    requestActions: ((request?: FrameType) => IPromise<IEpticaJsonResponse>)[],
    refuseReason?: string
  ): IPromise<IEpticaJsonResponse> {
    this.showActions = false;

    return this.mainPromise().then(request => {
      let promise = this.$q.resolve<IEpticaJsonResponse | undefined>(undefined);
      let currentActionIndex = 0;
      for (let currentAction of requestActions) {
        promise = promise.then(() => currentAction(request));

        if (request instanceof EpticaRequest) {
          promise.catch(e => {
            if (e && e.cause === REJECTION_PENDING_ASK_FOR_HELP) {
              // Send was not done because the popup that asks to confirm to close all the pending
              // assistance requests has opened. => override the vanilla behaviour by replacing the buttons
              if (currentActionIndex < requestActions.length - 1) {
                request.overrideClosePendingAssistJson(() =>
                  this.handleJsonActions(
                    [() => request.confirmClosePendingAssistJson(e.submitForm)].concat(requestActions.slice(currentActionIndex + 1))
                  )
                );
              } else {
                request.overrideClosePendingAssistJson(() =>
                  this.handleJsonAction(() => request.confirmClosePendingAssistJson(e.submitForm))
                );
              }
            }
            return this.$q.reject(e);
          });
        }

        currentActionIndex++;
      }
      return promise.then(
        epticaJson => this.doPostJsonActions(request, epticaJson!).then(e => this.handleJsonSuccess(request, e, refuseReason)),
        (errorJsonArray: IEpticaJsonResponse[]) => this.showFrameAndReject(request, errorJsonArray),
        () => this.$log.debug('Action finished')
      );
    });
  }

  protected handleJsonSuccess(
    request: FrameType,
    epticaJson: IEpticaJsonResponse,
    refuseReason?: string,
    isSuspend: boolean = false,
    isApproval: boolean = false
  ): IEpticaJsonResponse {
    EpticaHelperJson.displayActionMessages(this.toastDeprecatedService, epticaJson);
    this.endContact(this.responseId, Option.of(refuseReason), isSuspend, isApproval).finally(() => request.finish());
    return epticaJson;
  }

  protected showFrameAndReject(request: FrameType, epticaJsonArray: IEpticaJsonResponse[]): IPromise<IEpticaJsonResponse> {
    request.show();
    this.showActions = true;
    if (epticaJsonArray) {
      for (let epticaJson of epticaJsonArray) {
        EpticaHelperJson.displayActionMessages(this.toastDeprecatedService, epticaJson);
      }
    }
    return this.$q.reject(request);
  }

  /**
   * Do a action that does not reload eptica's frame and change ui state
   */
  protected doJsAction(commands: any[], requestAction: (request: FrameType) => boolean): void {
    this.mainPromise().then(request => {
      if (requestAction(request)) {
        this.router.navigateFromLastChild(commands);
      }
    });
  }

  /**
   * Search for the Id of the created request, store it in the related contact entry
   * */
  protected doPostJsonActions(
    _: FrameType,
    epticaJsonResponse: IEpticaJsonResponse,
    _refuseReason?: string
  ): IPromise<IEpticaJsonResponse> {
    return this.$q.resolve(epticaJsonResponse);
  }

  /**
   * End the contact normally
   */
  protected endContact(responseId: string, refuseReason: Option<string> = None, isSuspend = false, isApproval = false): IPromise<IContact> {
    if (this.mainPromise() != null) {
      this.mainPromise().then(request => request.hide());
    }
    return this.contactDeprecatedService.getContact(responseId).then(() => {
      if (refuseReason.isDefined() && refuseReason.get() !== 'APPROVAL_ACCEPTED') {
        return this.contactDeprecatedService.refuse(this.responseId, refuseReason.get());
      }
      return this.contactDeprecatedService.close(this.responseId, isSuspend, isApproval);
    });
  }

  /**
   * Say to Business that we defer the response with reply to increment metrics
   */
  protected deferWithResponse(responseId: string): IPromise<IContact> {
    return this.contactDeprecatedService.deferWithResponse(responseId);
  }
}
